// NUMERO — como valores em dinheiro e quantidades estao escritos no arquivo.
//
// Cobre a dimensao `numeros` da matriz: decimal com virgula ou ponto, centavos
// implicitos, separador de milhar, zeros a esquerda e sinal. O formato e'
// DECLARADO pelo layout, nunca adivinhado do texto: "1.234" e' mil duzentos e
// trinta e quatro num arquivo com milhar por ponto, e um inteiro e duzentos e
// trinta e quatro milesimos num arquivo com decimal por ponto. So' a
// declaracao distingue, e adivinhar seria errar em silencio exatamente onde
// o dinheiro esta.
//
// `lerNumero` nunca lanca por causa do DADO: texto que nao cabe no formato
// declarado vira `null`, e o adaptador transforma em `descartadas[]` com
// motivo (regra 2 do adaptador). Lanca so' por formato contraditorio, que e'
// erro de quem declarou o layout, nao do arquivo.
/** Valor que o formato nao consegue escrever: negativo sem sinal, ou que nao cabe na largura. */
export class NumeroNaoRepresentavel extends Error {
    constructor(mensagem) {
        super(mensagem);
        this.name = "NumeroNaoRepresentavel";
    }
}
const CARACTERE = { ponto: ".", virgula: ",", espaco: " " };
function separadorDecimal(f) {
    return f.decimal === "virgula" ? "," : f.decimal === "ponto" ? "." : null;
}
function separadorMilhar(f) {
    const m = f.milhar ?? "nenhum";
    return m === "nenhum" ? null : CARACTERE[m];
}
/** Milhar e decimal com o mesmo caractere nao tem leitura possivel — e' erro de declaracao. */
function conferirFormato(f) {
    const milhar = separadorMilhar(f);
    if (milhar !== null && milhar === separadorDecimal(f)) {
        throw new Error(`forma/numero: milhar e decimal com o mesmo separador "${milhar}"`);
    }
    if (f.casas !== undefined && (!Number.isInteger(f.casas) || f.casas < 0)) {
        throw new Error(`forma/numero: casas ${f.casas} — precisa ser inteiro >= 0`);
    }
}
/**
 * O numero, ou `null` quando o texto nao cabe no formato declarado.
 *
 * Aceita zeros a esquerda sempre; sinal so' onde o formato diz (sinal no
 * lugar errado e' `null`, nao "quase certo"); sufixo ausente e' positivo;
 * milhar so' na parte inteira. Centavos implicitos dividem por 100 — nunca
 * ha ponto no arquivo.
 */
export function lerNumero(texto, f) {
    conferirFormato(f);
    let s = texto.trim();
    if (s === "")
        return null;
    let negativo = false;
    const sinal = f.sinal ?? "prefixo";
    if (sinal === "sufixo") {
        const ultimo = s[s.length - 1];
        if (ultimo === "-" || ultimo === "+") {
            negativo = ultimo === "-";
            s = s.slice(0, -1);
        }
    }
    else if (sinal === "prefixo") {
        const primeiro = s[0];
        if (primeiro === "-" || primeiro === "+") {
            negativo = primeiro === "-";
            s = s.slice(1);
        }
    }
    // `nenhum`: um sinal que sobrou cai na conferencia de digitos abaixo.
    const decimal = separadorDecimal(f);
    let inteiro = s;
    let fracao = "";
    if (decimal !== null) {
        const partes = s.split(decimal);
        if (partes.length > 2)
            return null;
        inteiro = partes[0];
        fracao = partes[1] ?? "";
        if (partes.length === 2 && fracao === "")
            return null;
    }
    const milhar = separadorMilhar(f);
    if (milhar !== null)
        inteiro = inteiro.split(milhar).join("");
    if (inteiro === "" && fracao === "")
        return null;
    if (!/^\d*$/.test(inteiro) || !/^\d*$/.test(fracao))
        return null;
    if (inteiro === "")
        inteiro = "0";
    const valor = f.decimal === "implicito-2" ? Number(inteiro) / 100 : Number(`${inteiro}.${fracao || "0"}`);
    if (!Number.isFinite(valor))
        return null;
    const assinado = negativo ? -valor : valor;
    // -0 nao existe em folha; e `Object.is(-0, 0)` e' falso nos testes.
    return assinado === 0 ? 0 : assinado;
}
/** Arredonda em `casas` sem o vies de `toFixed` (1.005 → 1.01, nao 1.00). */
function arredondar(valor, casas) {
    const fator = 10 ** casas;
    return Math.round((valor + Number.EPSILON) * fator) / fator;
}
function agrupar(inteiro, milhar) {
    return milhar === null ? inteiro : inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, milhar);
}
function completar(corpo, largura, zeros) {
    if (corpo.length > largura) {
        throw new NumeroNaoRepresentavel(`"${corpo}" tem ${corpo.length} caracteres e nao cabe em ${largura} posicoes`);
    }
    return corpo.padStart(largura, zeros ? "0" : " ");
}
/**
 * O texto de um numero no formato. `largura` completa a esquerda (zeros por
 * padrao, espacos com `zerosAEsquerda: false`) e o sinal conta na largura:
 * com zeros, o "-" ocupa a primeira posicao ("-000123", como no CNAB); com
 * espacos, encosta no numero ("   -123", como impresso).
 *
 * Lanca `NumeroNaoRepresentavel` quando o formato nao consegue dizer o valor:
 * negativo com `sinal: "nenhum"`, ou texto maior que `largura`. Cortar seria
 * mandar outro valor para a folha.
 */
export function escreverNumero(valor, f, largura) {
    conferirFormato(f);
    if (!Number.isFinite(valor))
        throw new NumeroNaoRepresentavel(`${valor} nao e' um numero finito`);
    const sinal = f.sinal ?? "prefixo";
    const negativo = valor < 0;
    if (negativo && sinal === "nenhum") {
        throw new NumeroNaoRepresentavel(`${valor}: o formato nao tem sinal e o valor e' negativo`);
    }
    const absoluto = Math.abs(valor);
    let corpo;
    if (f.decimal === "implicito-2") {
        corpo = String(Math.round((absoluto + Number.EPSILON) * 100));
    }
    else {
        const casas = f.casas ?? 2;
        const [inteiro, fracao] = arredondar(absoluto, casas).toFixed(casas).split(".");
        corpo = agrupar(inteiro, separadorMilhar(f)) + (casas > 0 ? separadorDecimal(f) + fracao : "");
    }
    if (largura === undefined) {
        if (sinal === "sufixo")
            return `${corpo}${negativo ? "-" : "+"}`;
        return negativo ? `-${corpo}` : corpo;
    }
    const zeros = f.zerosAEsquerda ?? true;
    if (sinal === "sufixo")
        return completar(corpo, largura - 1, zeros) + (negativo ? "-" : "+");
    if (negativo)
        return zeros ? `-${completar(corpo, largura - 1, true)}` : completar(`-${corpo}`, largura, false);
    return completar(corpo, largura, zeros);
}
//# sourceMappingURL=numero.js.map